import {
  createEmailTemplate,
  getAllEmailTemplates,
  getEmailTemplateById,
  getExistingEmailTemplate,
  getTotalActiveEmailTemplates,
  listAllEmailTemplates,
  updateEmailTemplate,
  updateManyEmailTemplates,
} from '@/api/email-template/email-template';
import {
  ListAllEmailTemplate,
  TotalActiveEmailTemplate,
} from '@/api/entities/email-template';
import { EmailTemplateFilter } from '@/entities/email-template';
import { TEmailTemplateSchema } from '@/schemas/email-template/email-template.schema';
import { TCustomQueryProps } from '@/type/hooks.types';
import { ErrorResponse } from '@/utils/axios.http';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notify } from '@/utils/toast.utils';
import { NOTIFY } from '@/utils/constant.utils';

export const useCreateEmailTemplate = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TEmailTemplateSchema) => createEmailTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getAllEmailTemplates'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getTotalActiveEmailTemplates'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getExistingEmailTemplate'],
      });
      queryClient.invalidateQueries({
        queryKey: ['listAllEmailTemplates'],
      });

      notify(t('common:templateCreatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useGetExistingEmailTemplate = (query?: TEmailTemplateSchema) => {
  return useQuery({
    queryKey: ['getExistingEmailTemplate', query],
    queryFn: () => getExistingEmailTemplate(query),
  });
};

export const useListAllEmailTemplates = (query?: ListAllEmailTemplate) => {
  return useQuery({
    queryKey: ['listAllEmailTemplates', query],
    queryFn: () => listAllEmailTemplates(query),
  });
};

export const useGetAllEmailTemplates = (
  filter: EmailTemplateFilter,
  page: number,
  limit: number,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc',
) => {
  return useQuery({
    queryKey: ['getAllEmailTemplates', filter, page, sortBy, sortDirection],
    queryFn: () =>
      getAllEmailTemplates(filter, page, limit, sortBy, sortDirection),
  });
};

export const useGetTotalActiveEmailTemplates = (
  query?: TotalActiveEmailTemplate,
) => {
  return useQuery({
    queryKey: ['getTotalActiveEmailTemplates', query],
    queryFn: () => getTotalActiveEmailTemplates(query),
  });
};

export const useGetEmailTemplateById = (id: string) => {
  return useQuery({
    queryKey: ['getEmailTemplateById', id],
    queryFn: () => getEmailTemplateById(id),
    enabled: !!id,
  });
};

export const useUpdateEmailTemplate = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (emailTemplate: TEmailTemplateSchema) =>
      updateEmailTemplate(emailTemplate),
    onSuccess: (_, variables) => {
      const { id } = variables;

      queryClient.invalidateQueries({
        queryKey: ['getAllEmailTemplates'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getTotalActiveEmailTemplates'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getExistingEmailTemplate'],
      });
      queryClient.invalidateQueries({
        queryKey: ['listAllEmailTemplates'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getEmailTemplateById', id],
      });

      notify(t('common:templateUpdatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating email template:', error);
    },
  });
};

export const useUpdateManyEmailTemplates = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (emailTemplates: TEmailTemplateSchema[]) =>
      updateManyEmailTemplates(emailTemplates),
    onSuccess: (_, variables) => {
      for (const item of variables) {
        queryClient.invalidateQueries({
          queryKey: ['getEmailTemplateById', item?.id],
        });
      }

      queryClient.invalidateQueries({
        queryKey: ['getAllEmailTemplates'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getTotalActiveEmailTemplates'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getExistingEmailTemplate'],
      });
      queryClient.invalidateQueries({
        queryKey: ['listAllEmailTemplates'],
      });

      notify(t('common:templateUpdatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating email templates:', error);
    },
  });
};

export const useGetEmailTemplatesSelectOptions = (
  query?: ListAllEmailTemplate,
) => {
  const { data: allEmailTemplates, isLoading: isLoadingAllEmailTemplates } =
    useListAllEmailTemplates(query);

  const getEmailTemplatesSelectOptions = () => {
    if (
      !isLoadingAllEmailTemplates &&
      allEmailTemplates &&
      allEmailTemplates?.data?.length > 0
    ) {
      return allEmailTemplates.data.map(
        (emailTemplate: TEmailTemplateSchema) => {
          // const { name, platformTemplateId } = emailTemplate;
          const { id, name } = emailTemplate;

          return {
            label: name,
            // value: platformTemplateId,
            value: id,
          };
        },
      );
    }

    return undefined;
  };

  return { getEmailTemplatesSelectOptions, isLoadingAllEmailTemplates };
};
